import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { RunsStore } from '@app/core/store/runs/runs.store';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { RUN_STATUS_FILTER_OPTIONS } from '@app/runs/runs-list/active-runs/run-filter-options';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { get } from 'lodash';
import { Observable, of } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { ColumnMinWidth, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';

export enum RunSearchColumnFields {
    Name = 'ExperimentName',
    Owner = 'Owner',
    Instrument = 'Instrument',
    Status = 'AggregateRunStatus',
    Created = 'TimeCreated',
    FileStatus = 'FileStatus'
}

export class RunSearchGridOptionsFormatter extends SearchGridOptionsFormatter<RunSearchColumnFields> {
    private instrumentFilterOptions$: Observable<FilterOption[]>;

    constructor(
        public resourceDictionaryService: SearchResourceDictionaryService,
        public bsshDatePipe: BsshDatePipe,
        public runsStore: RunsStore,
    ) {
        super(resourceDictionaryService, SearchResourceType.Run);

        this.instrumentFilterOptions$ = this.runsStore.stateWithPropertyChanges.pipe(
            filter(stateWithPropertyChanges => get(stateWithPropertyChanges, 'stateChanges.instrumentFilterOptions')),
            map(filteredState => filteredState.stateChanges.instrumentFilterOptions),
            shareReplay(1)
        );

        this.runsStore.loadInstrumentFilterOptions();
    }

    protected getOverrideColumnParameters(): OverrideColumnParameters<RunSearchColumnFields> {
        const inclusiveColumns: RunSearchColumnFields[] = [
            RunSearchColumnFields.Name,
            RunSearchColumnFields.Owner,
            RunSearchColumnFields.Instrument,
            RunSearchColumnFields.Created
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<RunSearchColumnFields>[] = [
            {
              colId: RunSearchColumnFields.Name,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions() {
        const defaultColumnDefinitions = [
            {
                headerName: 'Name',
                field: 'Run.ExperimentName',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params: any) => {
                    if(params.data) {
                        const cellRendererParams = {
                            linkText: params.data.Run.ExperimentName,
                            linkUrl: this.resourceConfig.uiHrefAccessor(params.data),
                            target: '_blank'
                        };

                        if(!this.isRowSelectable(params.node)) { 
                        cellRendererParams['tooltipText'] = this.getUnselectableRowMessage(params.node);
                        }
                        return cellRendererParams;
                    } else {
                        return {};
                    }
                },
                colId: RunSearchColumnFields.Name,
                sortable: false,
                flex: 4.5,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Owner',
                cellRenderer: 'span',
                field: 'Run.UserOwnedBy.Name',
                suppressSizeToFit: false,
                colId: RunSearchColumnFields.Owner,
                filterActive: false,
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Instrument',
                colId: RunSearchColumnFields.Instrument,
                field: 'Run.Instrument.SerialNumber',
                cellRenderer: 'tooltip',
                sortable: false,
                flex: 1.5,
                minWidth: ColumnMinWidth.RESOURCE,
                filterOptions: this.instrumentFilterOptions$,
                filterActive: true,
            },
            {
                headerName: 'Status',
                colId: RunSearchColumnFields.Status,
                field: 'Run.Status',
                cellRenderer: 'tooltip',
                sortable: false,
                minWidth: ColumnMinWidth.RUN_STATUS,
                filterOptions: of(RUN_STATUS_FILTER_OPTIONS),
                filterActive: true,
                flex: 2
              },
              {
                headerName: 'Created',
                colId: RunSearchColumnFields.Created,
                cellRenderer: 'tooltip',
                valueGetter: params => {
                  if (!params.data) { return; }
                  return this.bsshDatePipe.transform(params.data.Run.DateCreated);
                },
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.DATE
              }
        ];
        return defaultColumnDefinitions;
    }
}
